<template>
  <Sheet :open="modelValue" @update:open="$emit('update:modelValue', $event)">
    <SheetContent class="survey-drawer" side="right">
      <template v-if="survey">
        <SheetHeader>
          <SheetTitle>Детали отзыва</SheetTitle>
          <SheetDescription>
            {{ survey.property_name }}
            <span v-if="survey.property_city">· {{ survey.property_city }}</span>
          </SheetDescription>
        </SheetHeader>
        <Tabs v-model="drawerTab" class="min-w-0">
          <TabsList>
            <TabsTrigger value="details">Детали</TabsTrigger>
            <TabsTrigger value="history">История</TabsTrigger>
          </TabsList>
          <TabsContent value="details" class="drawer-content space-y-5">
            <dl class="survey-details">
              <div>
                <dt>Дата и время отзыва</dt>
                <dd>{{ formatDateTime(survey.date) }}</dd>
              </div>
              <div v-if="survey.ip">
                <dt>IP-адрес</dt>
                <dd>
                  {{ survey.ip }}
                  <span v-if="survey.city_name" class="text-muted-foreground">
                    ({{ survey.city_name }})
                  </span>
                </dd>
              </div>
              <div>
                <dt>Контактное лицо</dt>
                <dd>
                  <strong>{{ survey.contact_name }}</strong>
                  <p class="text-sm text-muted-foreground">{{ survey.position }}</p>
                </dd>
              </div>
              <div>
                <dt>Оценка</dt>
                <dd>
                  <Badge
                    :variant="
                      survey.vote >= 9
                        ? 'success'
                        : survey.vote >= 7
                          ? 'secondary'
                          : survey.vote >= 5
                            ? 'warning'
                            : 'destructive'
                    "
                  >
                    {{ survey.vote }} из 10
                  </Badge>
                </dd>
              </div>
              <div v-if="survey.notes">
                <dt>Отзыв клиента</dt>
                <dd class="notes-content">{{ survey.notes }}</dd>
              </div>
            </dl>
            <label class="ui-field">
              <span class="ui-label">Комментарий менеджера</span>
              <Textarea
                v-model="commentText"
                rows="5"
                placeholder="Добавьте комментарий…"
                :readonly="!canComment"
              />
            </label>
            <Button v-if="canComment" :disabled="savingComment" class="w-full" @click="saveComment">
              <Spinner v-if="savingComment" />
              <Save v-else :size="15" />
              Сохранить комментарий
            </Button>
          </TabsContent>
          <TabsContent value="history" class="space-y-5">
            <SurveyChart
              title="Динамика оценок"
              :property-id="survey.property_id"
              :show-period-filter="false"
              period="all_time"
            />
            <section>
              <h3 class="section-small-title">История оценок объекта</h3>
              <div v-if="loadingPropertyHistory" class="ui-empty">
                <Spinner />
                <span>Загрузка истории…</span>
              </div>
              <p v-else-if="!propertyHistory.length" class="ui-empty">Нет истории оценок</p>
              <div v-else class="space-y-3">
                <article
                  v-for="historyItem in propertyHistory"
                  :key="historyItem.id"
                  class="survey-history-item"
                  :class="{
                    'current-survey': historyItem.id === survey.id,
                    'contact-person-survey':
                      historyItem.contact_person_id === survey.contact_person_id &&
                      historyItem.id !== survey.id,
                  }"
                >
                  <div class="flex flex-wrap items-center gap-2">
                    <Badge
                      :variant="
                        historyItem.vote >= 9
                          ? 'success'
                          : historyItem.vote >= 7
                            ? 'secondary'
                            : historyItem.vote >= 5
                              ? 'warning'
                              : 'destructive'
                      "
                    >
                      {{ historyItem.vote }}
                    </Badge>
                    <time class="text-xs text-muted-foreground">
                      {{ formatDateTime(historyItem.date) }}
                    </time>
                    <Badge
                      v-if="historyItem.contact_person_id === survey.contact_person_id"
                      variant="outline"
                    >
                      Тот же контакт
                    </Badge>
                    <Badge v-if="historyItem.id === survey.id" variant="secondary">Текущий</Badge>
                  </div>
                  <p class="text-sm mt-2">
                    <strong>{{ historyItem.contact_name }}</strong>
                    <span v-if="historyItem.position" class="text-muted-foreground">
                      · {{ historyItem.position }}
                    </span>
                  </p>
                  <p
                    v-if="historyItem.city_name || historyItem.ip"
                    class="text-xs text-muted-foreground mt-1"
                  >
                    {{ historyItem.city_name || historyItem.ip }}
                  </p>
                  <p v-if="historyItem.notes" class="notes-content mt-3">{{ historyItem.notes }}</p>
                  <div v-if="historyItem.comments" class="border-t pt-3 mt-3">
                    <h4 class="text-xs text-muted-foreground mb-1">Комментарий менеджера</h4>
                    <p class="notes-content">{{ historyItem.comments }}</p>
                  </div>
                </article>
              </div>
            </section>
          </TabsContent>
        </Tabs>
      </template>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Save } from '@lucide/vue';

import { ref, watch } from 'vue';
import axios from 'axios';
import { dateTimeToDateWithTimeString } from '@/filters/date_filters';
import SurveyChart from '@/components/SurveyChart.vue';

interface Survey {
  id: number;
  date: string;
  property_name: string;
  property_id?: number;
  property_city?: string;
  position: string;
  contact_name: string;
  contact_person_id?: number;
  vote: number;
  notes: string;
  comments: string;
  ip: string | null;
  city_name?: string;
}

interface Props {
  modelValue: boolean;
  survey: Survey | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'survey-updated': [];
}>();

const drawerTab = ref('details');
const loadingPropertyHistory = ref(false);
const propertyHistory = ref<Survey[]>([]);
const savingComment = ref(false);
const canComment = ref(false);
const commentText = ref('');

function formatDateTime(dateTime: string): string {
  return dateTimeToDateWithTimeString(dateTime) || '';
}

async function loadPropertyHistory(propertyId: number) {
  loadingPropertyHistory.value = true;
  try {
    const response = await axios.get('/api/surveys/property_history/', {
      params: { property_id: propertyId },
    });
    propertyHistory.value = response.data;
  } catch (e) {
    console.error('Error loading property history:', e);
    propertyHistory.value = [];
  } finally {
    loadingPropertyHistory.value = false;
  }
}

async function checkCommentPermission() {
  try {
    const permResponse = await axios.get('/api/surveys/permissions/');
    canComment.value = permResponse.data.can_comment || false;
  } catch (e) {
    canComment.value = false;
  }
}

async function saveComment() {
  if (!props.survey) return;

  savingComment.value = true;
  try {
    await axios.patch(`/api/surveys/${props.survey.id}/add_comment/`, {
      comments: commentText.value,
    });
    emit('survey-updated');
  } catch (e) {
    console.error('Error saving comment:', e);
  } finally {
    savingComment.value = false;
  }
}

watch(
  () => props.survey,
  (newSurvey) => {
    if (newSurvey) {
      commentText.value = newSurvey.comments || '';
      drawerTab.value = 'details';
      checkCommentPermission();

      if (newSurvey.property_id) {
        loadPropertyHistory(newSurvey.property_id);
      }
    }
  },
  { immediate: true }
);
</script>
